/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  useEffect,
  useCallback,
  useMemo,
  useReduction,
  useRef,
} from '@web-stories-wp/react';
import {
  trackError,
  trackEvent,
  getTimeTracker,
} from '@web-stories-wp/tracking';
import {
  createBlob,
  getFileName,
  getImageDimensions,
  isAnimatedGif,
  isBlobURL,
  revokeBlob,
} from '@web-stories-wp/media';

/**
 * Internal dependencies
 */
import { useUploader } from '../../../uploader';
import { noop } from '../../../../utils/noop';
import useUploadVideoFrame from '../useUploadVideoFrame';
import useFFmpeg from '../useFFmpeg';
import getResourceFromLocalFile from '../getResourceFromLocalFile';
import { reducer } from './reducer';
import { QueueItemState, type Actions, type QueueState } from './types';
import preloadVideoMetadata from '../../../../../../media/src/preloadVideoMetadata';
import { logUploadError } from '@src/utils/upload/loggingUtils';
import { getIncompleteVideoError } from '@src/utils/upload/videoCompleteness';
import { isSlowConnection } from '@src/utils/upload/uploadResilience';

const initialState: QueueState = {
  queue: [],
};

const REVOKE_DELAY_MS = 2000;
const REVOKE_RETRY_MS = 1000;
const MAX_REVOCATION_AGE_MS = 5 * 60 * 1000;

type ResourceSnapshot = {
  src?: string;
  poster?: string;
};

type RevocationEntry = {
  timeoutId: ReturnType<typeof setTimeout>;
  scheduledAt: number;
  attempts: number;
};

function useMediaUploadQueue() {
  const {
    actions: { uploadBuffer, uploadFile },
  } = useUploader();
  const {
    isTranscodingEnabled,
    canTranscodeFile,
    transcodeVideo,
    stripAudioFromVideo,
    getFirstFrameOfVideo,
    convertGifToVideo,
    trimVideo,
  } = useFFmpeg();

  const [_state, actions] = useReduction(initialState, reducer);
  const state = _state as QueueState;
  const { uploadVideoPoster } = useUploadVideoFrame({
    updateMediaElement: noop,
  });
  const {
    startUploading,
    finishUploading,
    cancelUploading,
    startTranscoding,
    startMuting,
    startTrimming,
    finishTranscoding,
    finishMuting,
    finishTrimming,
    replacePlaceholderResource,
    updateProgress,
  } = actions as Actions;

  // Track which items have been processed in their current state
  const processedItemsRef = useRef(new Map<string, string>());
  const resourceSnapshotRef = useRef<Map<string, ResourceSnapshot>>(new Map());
  const pendingRevocationsRef = useRef<Map<string, RevocationEntry>>(
    new Map()
  );
  const queueRef = useRef(state.queue);
  queueRef.current = state.queue;

  const scheduleBlobRevocation = useCallback((url?: string) => {
    if (!url || !isBlobURL(url)) {
      return;
    }

    if (pendingRevocationsRef.current.has(url)) {
      return;
    }

    const scheduledAt = Date.now();

    const attemptRevocation = (attempt: number) => {
      const ageMs = Date.now() - scheduledAt;
      const isStillInQueue = queueRef.current.some(
        (item) =>
          item.resource?.src === url || item.resource?.poster === url
      );

      if (isStillInQueue && ageMs < MAX_REVOCATION_AGE_MS) {
        const timeoutId = setTimeout(
          () => attemptRevocation(attempt + 1),
          REVOKE_RETRY_MS
        );
        pendingRevocationsRef.current.set(url, {
          timeoutId,
          scheduledAt,
          attempts: attempt + 1,
        });
        return;
      }

      revokeBlob(url);
      pendingRevocationsRef.current.delete(url);
    };

    const timeoutId = setTimeout(
      () => attemptRevocation(1),
      REVOKE_DELAY_MS
    );
    pendingRevocationsRef.current.set(url, {
      timeoutId,
      scheduledAt,
      attempts: 1,
    });
  }, [isBlobURL, revokeBlob]);

  useEffect(() => {
    return () => {
      for (const [url, entry] of pendingRevocationsRef.current) {
        clearTimeout(entry.timeoutId);
        revokeBlob(url);
      }
      pendingRevocationsRef.current.clear();
    };
  }, [revokeBlob]);

  // Try to update placeholder resources for freshly transcoded file if still missing.
  useEffect(() => {
    async function updateItems() {
      await Promise.all(
        state.queue.map(async (item) => {
          const { id, file, state: itemState, resource } = item;
          if (
            itemState !== QueueItemState.TRANSCODED ||
            !resource.isPlaceholder ||
            resource.poster
          ) {
            return;
          }

          try {
            const { resource: newResource, posterFile } =
              await getResourceFromLocalFile(file);

            replacePlaceholderResource({
              id,
              resource: newResource,
              posterFile,
            });
          } catch {
            // Not interested in errors here.
          }
        })
      );
    }

    updateItems();
  }, [state.queue, replacePlaceholderResource]);

  // Clean up processed items tracking when items change state or are removed
  useEffect(() => {
    const currentItemKeys = new Set(
      state.queue.map((item) => `${item.id}-${item.state}`)
    );

    // Remove tracking for items that are no longer in the queue or have changed state
    for (const [key] of processedItemsRef.current) {
      if (!currentItemKeys.has(key)) {
        processedItemsRef.current.delete(key);
      }
    }
  }, [state.queue]);

  // Defer blob revocations so in-progress drag previews are not torn down mid-render.
  useEffect(() => {
    const nextResources = new Map<string, ResourceSnapshot>();

    state.queue.forEach((item) => {
      nextResources.set(item.id, {
        src: item.resource?.src,
        poster: item.resource?.poster,
      });
    });

    for (const [id, prev] of resourceSnapshotRef.current) {
      const current = nextResources.get(id);
      if (!current) {
        continue;
      }

      if (prev.src && prev.src !== current.src) {
        scheduleBlobRevocation(prev.src);
      }

      if (prev.poster && prev.poster !== current.poster) {
        scheduleBlobRevocation(prev.poster);
      }
    }

    resourceSnapshotRef.current = nextResources;
  }, [state.queue, scheduleBlobRevocation]);

  // Try to get dimensions and poster for placeholder resources.
  // This way we can show something more meaningful to the user before transcoding has finished.
  useEffect(() => {
    async function updateItems() {
      await Promise.all(
        state.queue.map(async (item) => {
          const { id, file, state: itemState, resource } = item;
          if (QueueItemState.PENDING !== itemState || !resource.isPlaceholder) {
            return;
          }

          if (!isTranscodingEnabled || !canTranscodeFile(file)) {
            return;
          }

          try {
            const videoFrame = await getFirstFrameOfVideo(file);
            const poster = createBlob(videoFrame);
            const { width, height } = await getImageDimensions(poster);
            const newResource = {
              ...resource,
              poster,
              width,
              height,
            };
            replacePlaceholderResource({
              id,
              resource: newResource,
              posterFile: videoFrame,
            });
          } catch (error) {
            console.error('getFirstFrameOfVideoFailed', error);
          }
        })
      );
    }

    updateItems();
  }, [
    state.queue,
    isTranscodingEnabled,
    canTranscodeFile,
    getFirstFrameOfVideo,
    replacePlaceholderResource,
  ]);

  const processPoster = useCallback(
    async ({
      newResource,
      posterFileName,
      newPosterFile,
      resource,
      id,
      updateProgress,
    }) => {
      try {
        const { poster, posterId } = await uploadVideoPoster(
          newResource.id,
          posterFileName,
          newPosterFile,
          updateProgress
        );

        let newResourceWithPoster = {
          ...newResource,
          poster: poster || newResource.poster || resource.poster,
          posterId,
        };

        if (resource.mimeType === 'image/gif') {
          newResourceWithPoster = {
            ...newResourceWithPoster,
            output: {
              ...newResourceWithPoster.output,
              poster: poster || newResource.poster || resource.poster,
            },
          };
        }

        finishUploading({
          id,
          resource: newResourceWithPoster,
        });
      } catch (error) {
        console.error('processPosterFailed', error);
        finishUploading({
          id,
          resource: newResource,
        });
      }
    },
    [finishUploading, uploadVideoPoster]
  );

  // Upload files to server, optionally first transcoding them.
  useEffect(() => {
    async function uploadItems() {
      /**
       * Uploads a single pending item.
       *
       * @param {Object} item Queue item.
       * @param {File} item.file File object.
       * @return {Promise<void>}
       */
      const uploadSingleItem = async (item) => {
        const {
          id,
          isBuffer,
          file,
          state: itemState,
          resource,
          additionalData = {},
          posterFile,
          muteVideo,
          trimData,
        } = item;

        if (QueueItemState.PENDING !== itemState) {
          return;
        }

        // Check if this item has already been processed in its current state
        const itemStateKey = `${id}-${itemState}`;
        if (processedItemsRef.current.has(itemStateKey)) {
          return;
        }

        // Mark this item as being processed in its current state
        processedItemsRef.current.set(itemStateKey, itemState);

        const trackTiming = getTimeTracker('load_upload_media');
        let progress = 10; // Initialize with non-0 value so loading bar can start showing
        let uploadStartProgress = 0; // Track when upload actually starts

        const updateProgressCallback = (
          uploadProgress,
          maxRemainingProgress
        ) => {
          // FIXED: Calculate progress linearly from the start of upload phase
          // If this is the first upload progress, record the starting point
          if (uploadStartProgress === 0) {
            uploadStartProgress = progress;
          }

          const progressRange = maxRemainingProgress - uploadStartProgress;
          const relativeProgress =
            uploadStartProgress + (uploadProgress / 100) * progressRange;

          progress = relativeProgress;

          updateProgress({ id, progress });
        };

        if (isBuffer) {
          try {
            startUploading({ id });
            updateProgress({ id, progress });

            const newResourceWithAssetUrl = await uploadBuffer(
              id,
              resource,
              (progress) => updateProgressCallback(progress, 100)
            );

            finishUploading({
              id,
              resource: newResourceWithAssetUrl,
            });
          } catch (error) {
            // Cancel uploading if there were any errors.
            cancelUploading({ id });

            trackError(`upload_media: ${id}`, error?.message);

            logUploadError('Studio: Upload Buffer Failed', file, error, {
              correlationId: id,
            });
          } finally {
            trackTiming();
          }

          return;
        }

        let newResource;
        let newFile = file;
        let newPosterFile = posterFile;

        if (file.type.includes('video')) {
          try {
            await preloadVideoMetadata(URL.createObjectURL(file));
          } catch (error) {
            // A cut video often can't load its metadata at all (an MP4 whose
            // index was at the end loses it): tell the editor why.
            const incomplete = await getIncompleteVideoError(id, file);
            cancelUploading({
              id,
              ...(incomplete ? { error: incomplete } : {}),
            });
            trackError(`upload_media: ${id}`, error?.message);

            logUploadError(
              'Studio: Preload Video Metadata Failed',
              file,
              error,
              {
                correlationId: id,
              }
            );

            return;
          }
        }

        // Convert animated GIFs to videos if possible.
        if (
          isTranscodingEnabled &&
          resource.mimeType === 'image/gif' &&
          isAnimatedGif(await file.arrayBuffer())
        ) {
          startTranscoding({ id });
          updateProgress({ id, progress });

          try {
            newFile = await convertGifToVideo(file);
            finishTranscoding({ id, file: newFile });
            additionalData.media_source = 'gif-conversion';
            additionalData.no_audio = true;

            progress += 10;
            updateProgress({ id, progress });
          } catch (error) {
            // Cancel uploading if there were any errors.
            cancelUploading({ id });

            trackError(`upload_media: ${id}`, error?.message);

            logUploadError('Studio: GIF Conversion Failed', file, error, {
              correlationId: id,
            });

            return;
          }

          try {
            newPosterFile = await getFirstFrameOfVideo(newFile);

            progress += 10;
            updateProgress({ id, progress });
          } catch (error) {
            // Do nothing here.
          }
        }

        // Transcode/Optimize videos before upload.
        // TODO: Only transcode & optimize video if needed (criteria TBD).
        // Probably need to use FFmpeg first to get more information (dimensions, fps, etc.)
        if (isTranscodingEnabled && canTranscodeFile(file)) {
          if (trimData) {
            startTrimming({ id });
            updateProgress({ id, progress });

            try {
              newFile = await trimVideo(file, trimData.start, trimData.end);
              finishTrimming({ id, file: newFile });
              additionalData.meta = {
                web_stories_trim_data: trimData,
              };

              progress += 5;
              updateProgress({ id, progress });
            } catch (error) {
              // Cancel uploading if there were any errors.
              cancelUploading({ id });

              trackError(`upload_media: ${id}`, error?.message);

              logUploadError('Studio: Trim Video Failed', file, error, {
                correlationId: id,
              });

              return;
            }
          } else if (muteVideo) {
            startMuting({ id });
            updateProgress({ id, progress });

            try {
              newFile = await stripAudioFromVideo(file);
              finishMuting({ id, file: newFile });
              additionalData.no_audio = true;

              progress += 5;
              updateProgress({ id, progress });
            } catch (error) {
              // Cancel uploading if there were any errors.
              cancelUploading({ id });

              trackError(`upload_media: ${id}`, error?.message);

              logUploadError('Studio: Muting Video Failed', file, error, {
                correlationId: id,
              });

              return;
            }
          } else {
            startTranscoding({ id });
            updateProgress({ id, progress });

            try {
              newFile = await transcodeVideo(file);
              finishTranscoding({ id, file: newFile });
              additionalData.media_source = 'video-optimization';

              progress += 5;
              updateProgress({ id, progress });
            } catch (error) {
              // Cancel uploading if there were any errors.
              cancelUploading({ id });

              trackError(`upload_media: ${id}`, error?.message);

              logUploadError('Studio: Transcoding Video Failed', file, error, {
                correlationId: id,
              });

              return;
            }
          }
        }

        startUploading({ id });
        updateProgress({ id, progress });

        trackEvent('upload_media', {
          file_size: newFile.size,
          file_type: newFile.type,
        });

        const shouldUploadPoster = !file.type.includes('audio');

        try {
          // The newly uploaded file won't have a poster yet.
          // However, we'll likely still have one on file.
          // Add it back so we're never without one.
          // The final poster will be uploaded later by uploadVideoPoster().
          newResource = await uploadFile(
            id,
            newFile,
            additionalData,
            (progress) =>
              updateProgressCallback(progress, shouldUploadPoster ? 85 : 100) // Leave 15% for poster processing
          );

          if (!shouldUploadPoster && newResource?.id) {
            progress = 100;
            updateProgress({ id, progress });

            finishUploading({
              id,
              resource: {
                ...newResource,
                ...(file.type.includes('audio')
                  ? {
                      length: resource.length,
                      lengthFormatted: resource.lengthFormatted,
                    }
                  : {}),
              },
            });
          }
        } catch (error) {
          // Cancel uploading if there were any errors. An error written for the
          // editor replaces the generic failure message.
          cancelUploading({
            id,
            ...(error?.isUserError ? { error } : {}),
          });
          trackError(`upload_media: ${id}`, error?.message);

          logUploadError('Studio: Upload File Failed', file, error, {
            correlationId: id,
          });
        } finally {
          trackTiming();
        }

        const posterFileName = `${getFileName(file)}-poster.jpeg`;

        if (shouldUploadPoster && newResource?.id) {
          await processPoster({
            newResource,
            posterFileName,
            newPosterFile,
            resource,
            id,
            updateProgress: (progress) => updateProgressCallback(progress, 95), // Leave 5% for the poster preloading
          });
        }

        progress = 100;
        updateProgress({ id, progress });
      };

      const shouldUploadSequentially = isSlowConnection();
      const pendingItems = state.queue.filter(
        (item) => item.state === QueueItemState.PENDING
      );

      // Process uploads either sequentially (for slow connections) or in parallel (for fast connections)
      if (shouldUploadSequentially) {
        for (const item of pendingItems) {
          await uploadSingleItem(item);
        }
      } else {
        await Promise.all(pendingItems.map(uploadSingleItem));
      }
    }

    uploadItems();
  }, [
    state.queue,
    cancelUploading,
    uploadBuffer,
    uploadFile,
    startUploading,
    finishUploading,
    processPoster,
    startTranscoding,
    finishTranscoding,
    isTranscodingEnabled,
    getFirstFrameOfVideo,
    canTranscodeFile,
    transcodeVideo,
    stripAudioFromVideo,
    convertGifToVideo,
    startMuting,
    finishMuting,
    trimVideo,
    startTrimming,
    finishTrimming,
    updateProgress,
  ]);

  return useMemo(() => {
    let progressForAllItems = 100;
    const itemsInProgress = state.queue.filter((item) => item.progress < 100);
    const totalItemsInProgress = itemsInProgress.length;

    if (totalItemsInProgress > 0) {
      const totalProgress = itemsInProgress.reduce((acc, item) => {
        if (isNaN(item.progress)) {
          return acc;
        }

        return acc + item.progress;
      }, 0);

      progressForAllItems = totalProgress / totalItemsInProgress;
    }

    return {
      state: {
        progress: state.queue.filter(
          (item) =>
            ![
              QueueItemState.UPLOADED,
              QueueItemState.CANCELLED,
              QueueItemState.PENDING,
            ].includes(item.state)
        ),
        progressForAllItems,
        pending: state.queue.filter(
          (item) => item.state === QueueItemState.PENDING
        ),
        uploaded: state.queue.filter(
          (item) => item.state === QueueItemState.UPLOADED
        ),
        failures: state.queue.filter(
          (item) => item.state === QueueItemState.CANCELLED
        ),
        isUploading: state.queue.some(
          (item) =>
            ![
              QueueItemState.UPLOADED,
              QueueItemState.CANCELLED,
              QueueItemState.PENDING,
            ].includes(item.state)
        ),
        isTranscoding: state.queue.some(
          (item) => item.state === QueueItemState.TRANSCODING
        ),
        isMuting: state.queue.some(
          (item) => item.state === QueueItemState.MUTING
        ),
        isTrimming: state.queue.some(
          (item) => item.state === QueueItemState.TRIMMING
        ),
      },
      actions: {
        addItem: actions.addItem,
        removeItem: actions.removeItem,
      },
    };
  }, [state, actions]);
}

export default useMediaUploadQueue;
